<template>
  <div :class="columnClassName">
    <column-header :activeLayout="activeLayout" :columns="columns" :columnIndex="columnIndex" :components="components" :activeComponent="component" v-on:setActiveComponent="setActiveComponent"></column-header>
    <div class="openlist-transaction-container" v-if="transaction">
      <transition name="fade">
        <div class="openlist-transaction" v-show="transaction">
          <h3><i class="fa fa-fw fa-shopping-cart"></i> <template v-if="isConfirmedOrder(transaction)">{{ _('Confirmed order') }}</template><template v-else>{{ _('Open order') }}</template> {{ transaction.id }}</h3>
          <lock-info v-if="locked" :locked="locked" :lockStatus="lockStatus" v-on:forceLock="getComponentLock(true, 'OrderDetails')" />
          <div>{{ transactionFullDate }}</div>
          <div v-if="transaction.name">{{ transaction.name }}</div>
          <div v-if="transaction.name">{{ transaction.company }}</div>
          <div v-if="transaction.name">{{ transaction.ident }}</div>
          <div v-if="transaction.name">{{ transaction.address }}</div>
          <div v-if="transaction.name">{{ transaction.zip }}</div>
          <div v-if="transaction.name">{{ transaction.city }}</div>
          <div v-if="transaction.name">{{ transaction.country }}</div>
          <div v-if="transaction.name">{{ transaction.phone }}</div>
          <div v-if="transaction.name">{{ transaction.email }}</div>
          <div v-else>{{ _('Johku App') }}</div>
          <h4>{{ $filters.currency(transaction.totalWithDelivery, settings.currency) }}</h4>
          <template v-if="transaction.more">
            <hr />
            <h4>{{ _('Additional info') }}</h4>
            <div>{{ transaction.more }}</div>
          </template>
          <template v-if="transaction.notes">
            <hr />
            <h4>{{ _('Notes') }}</h4>
            <div>{{ transaction.notes }}</div>
          </template>
          <br />
        </div>
      </transition>
      <transition name="fade">
        <div v-show="items" class="receipt-items">
          <cart-item parent="openlist" :readonly="true" :settings="shopSettings(shopId)" :shopId="shopId" :baseDomain="baseDomain" :locale="locale" :currItem="item" v-for="item in items.filter(i => !i.discount_id)" :key="item.id" leftcolwidth="0" rightcolwidth="12" context="page"></cart-item>
        </div>
      </transition>
      <transition name="fade">
        <div class="buttons button-row" v-show="transaction">
          <button class="btn btn-success" v-on:click="processOrder" :disabled="!isProcessOrderEnabled">{{ _('Process order') }}</button>
          <button class="btn btn-default" v-on:click="closeTransaction">{{ _('Close') }}</button>
        </div>
      </transition>
    </div>
    <div class="openlist-container tinylist-container" v-else>
      <transition name="fade">
        <div class="input-group">
          <input type="text" class="form-control" v-on:keyup="search" :placeholder="searchHint" v-model="searchStr" />
          <span class="input-group-btn">
            <button class="btn btn-default" type="button" v-on:click="clearSearch"><i class="fa fa-fw fa-trash"></i></button>
          </span>
        </div>
      </transition>
      <transition name="fade">
        <div class="openlist-items tinylist-items" v-show="transactions.length">
          <transaction-item v-for="(transaction, index) in transactions" :baseDomain="baseDomain" :key="transaction.id" :index="index" :transaction="transaction" v-on:transactionClicked="openTransaction(transaction)" v-on:notesClicked="displayNotes(transaction)"></transaction-item>
        </div>
      </transition>
      <transition name="fade">
        <div class="buttons button-row" v-show="transactions.length || start > 0">
          <button class="btn btn-default" :disabled="start === 0" v-on:click="loadPrevious">{{ _('Previous') }}</button>
          <button class="btn btn-default" :disabled="!(transactions.length >= limit)" v-on:click="loadNext">{{ _('Next') }}</button>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>

import axios from 'axios'
import { DateTime } from 'luxon'
import components from '~/lib/components'
import TransactionItem from '~/components/TransactionItem'
import CartItem from '~/components/CartItem'
import debounce from 'lodash/debounce'
import throttle from 'lodash/throttle'
import { mapGetters } from 'vuex'
import constants from '~/lib/constants'

export default {
  data () {
    return {
      transactions: [],
      transaction: null,
      items: null,
      limit: 15,
      start: 0,
      processing: false,
      searchStr: '',
    }
  },
  mixins: [components, constants],
  computed: {
    ...mapGetters(['activeBasket', 'basketIds']),
    searchHint () {
      return this._('Type to search open orders')
    },
    data () {
      return JSON.parse(this.component.data)
    },
    transactionFullDate () {
      return DateTime.fromSeconds(Number(this.transaction.orderdate), { zone: this.timezone }).toFormat(this.dateFormat) + ' ' + DateTime.fromSeconds(Number(this.transaction.orderdate), { zone: this.timezone }).toFormat(this.timeFormat)
    },
    isProcessOrderEnabled () {
      return !this.processing && !this.locked
    }
  },
  methods: {
    isConfirmedOrder (order) {
      if (order && parseInt(order.confirmed) === this.ORDER_CONFIRMED) {
        return true
      }
      return false
    },
    search () {
      this.doSearch(this)
    },
    clearSearch () {
      this.searchStr = ''
      this.search()
    },
    doSearch: debounce (($this) => {
      $this.start = 0
      $this.loadTransactions()
    }, 250),
    shopSettings (shopId) {
      return this.allSettings[shopId + '-' + this.locale] || {}
    },
    loadTransactionsThrottled: throttle(function () { this.loadTransactions() }, 500, { leading: false, trailing: true }),
    loadTransactions () {
      let $this = this
      let searchQuery = ''
      if (this.searchStr) {
        searchQuery = '&sSearch=' + encodeURIComponent(this.searchStr)
      }
      axios.get(this.baseDomain + this.shopId + '/orders.json?iSortCol_0=0&iSortingCols=1&sSortDir_0=desc&iDisplayLength=' + this.limit + '&iDisplayStart=' + this.start + searchQuery + '&openorconfirmed=true&apikey=' + this.apiKey)
      .then(function (response) {
        $this.transactions = response.data
      })
      .catch(function (e) {
        console.log('Failed to load transactions.')
      })
    },
    openTransaction (transaction) {
      let $this = this
      let billArr = [
        axios.get(this.baseDomain + this.shopId + '/orders/' + transaction.id + '.json?apikey=' + this.apiKey),
        axios.get(this.baseDomain + this.shopId + '/orders/' + transaction.id + '/items.json?reservations=true&apikey=' + this.apiKey)
      ]
      axios.all(billArr)
      .then(axios.spread(function (billResponse, itemResponse) {
        $this.transaction = billResponse.data
        $this.items = itemResponse.data
      }))
    },
    closeTransaction () {
      this.transaction = false
    },
    displayNotes (transaction) {
      this.EventBus.$emit('displayNotes', {
        type: 'order',
        transaction: transaction
      })
    },
    async processOrder () {
      if (!this.transaction) {
        return
      }
      this.EventBus.$emit('showReceiptComponent', { shopId: this.shopId, index: this.activeBasket })
      await nextTick()
      this.EventBus.$emit('modifyOrder', {
        order: this.transaction
      })
    },
    loadPrevious () {
      if (this.start >= this.limit) {
        this.start -= this.limit
      } else {
        this.start = 0
      }
      this.loadTransactions()
    },
    loadNext () {
      if (this.transactions.length >= this.limit) {
        this.start += this.limit
      }
      this.loadTransactions()
    },
    getComponentItemId () {
      if (this.transaction) {
        return this.transaction.id
      }
      return null
    }
  },
  watch: {
    transaction (newValue, oldValue) {
      if (newValue) {
        this.getComponentLock(false, 'OrderDetails')
      } else {
        if (oldValue && oldValue.id) {
          this.releaseComponentLock(oldValue.id, 'OrderDetails')
        }
      }
    }
  },
  components: {
    CartItem,
    TransactionItem
  },
  mounted () {
    this.loadTransactions()
    if(!this.events) {
      let events = {}
      events['orderUpdate'] = (message) => {
        this.loadTransactionsThrottled()
      }
      this.events = events
    }
    this.enableEventBus()
  }
}

</script>